<script lang="ts">
  import { onDestroy, onMount } from 'svelte';
  import { ArrowDownUp, ChevronsDown, ChevronsUp, RefreshCw } from 'lucide-svelte';
  import {
    type LiveMonitorAggregate,
    type LiveMonitorMode,
    type LiveSessionCard,
    fetchLiveMonitorSummary,
  } from '$lib/api/liveMonitor';
  import {
    type ParityStatus,
    fetchParityStatuses,
    fetchRenkoSessionIds,
  } from '$lib/api/sessions';
  import { Badge } from '$lib/components/ui/badge';
  import { Button } from '$lib/components/ui/button';
  import { Skeleton } from '$lib/components/ui/skeleton';
  import { liveLookStore } from '$lib/stores/liveLook.svelte';
  import {
    type UnrealizedPnLReason,
    livePriceStore,
    unrealizedPnL,
    unrealizedPnLReason,
  } from '$lib/stores/livePrice.svelte';
  import { type ScheduledTask, scheduleAlignedRefresh } from '$lib/utils/schedule';
  import { isSessionActiveNow } from '$lib/utils/active-window';
  import AppShell from './AppShell.svelte';
  import LiveMonitorAggregateBar from './LiveMonitorAggregateBar.svelte';
  import SessionCardCmp from './SessionCard.svelte';

  // ============================================================
  // LiveMonitor page - Fase 4.
  //
  // Reusada por las rutas /live y /developer via prop `mode`.
  // - mode='live'      -> developerMonitor !== true
  // - mode='developer' -> developerMonitor === true + theme cyan
  //
  // Cierra fixes del audit v1 §6:
  //   - 1 round-trip (endpoint $facet del backend, no N+1)
  //   - Live price polling con mutex anti-overlap (en livePriceStore)
  //   - "Open Only" filtra TAMBIEN el aggregate (no solo cards)
  //   - valuePerPoint sin default 20 (computado en SessionCard como N/A)
  // ============================================================

  interface Props {
    mode: LiveMonitorMode;
  }

  let { mode }: Props = $props();

  // ----- estado -----
  let allSessions = $state<LiveSessionCard[]>([]);
  let aggregate = $state<LiveMonitorAggregate | null>(null);
  let renkoSessionIds = $state<Set<string>>(new Set());
  let parityStatuses = $state<Map<string, ParityStatus>>(new Map());
  let loading = $state(true);
  let refreshing = $state(false);
  let error = $state<string | null>(null);
  let lastFetch = $state<Date | null>(null);

  // Filtros
  let selectedAccounts = $state<Set<string>>(new Set());
  let selectedSymbols = $state<Set<string>>(new Set());
  let selectedTitles = $state<Set<string>>(new Set());
  let sortByTime = $state(false);
  let openOnly = $state(false);
  let activeOnly = $state(false);

  // Expansion por sessionId
  let expandedIds = $state<Set<string>>(new Set());

  // ----- fetch -----
  let scheduled: ScheduledTask | null = null;

  async function fetchData(silent = false): Promise<void> {
    if (!silent) refreshing = true;
    try {
      // Fetch principal + parity + renko en paralelo (parity y renko son
      // livianos y permiten que badges WP:* y los botones renko aterricen
      // sin un round-trip extra al expandir).
      const [summaryRes, renkoRes, parityRes] = await Promise.allSettled([
        fetchLiveMonitorSummary(mode),
        fetchRenkoSessionIds(),
        fetchParityStatuses(),
      ]);

      if (summaryRes.status === 'fulfilled') {
        const res = summaryRes.value;
        allSessions = res.sessions;
        aggregate = res.aggregate;
        lastFetch = new Date(res.fetchedAt);
        error = null;

        // El refresh global de symbols vive en AppShell (union de live +
        // developer). Pero hay una carrera: en el primer render de /live
        // o /developer, si AppShell todavia no termino su fetch, el WS
        // no esta suscripto al symbol que necesita esta pagina y el
        // livePriceStore.prices.get(symbol) devuelve undefined hasta el
        // proximo refresh aligned :05.
        //
        // addSymbols suma sin pisar el set del shell (no borra prices
        // ni desuscribe lo que ya estaba), eliminando la carrera para
        // los symbols del modo actual.
        const symbols = new Set<string>();
        for (const s of res.sessions) {
          if (s.symbol) symbols.add(s.symbol);
        }
        livePriceStore.addSymbols(symbols);
      } else {
        console.error('[live-monitor] fetch fallo', summaryRes.reason);
        error =
          summaryRes.reason instanceof Error ? summaryRes.reason.message : 'Unknown error';
      }

      if (renkoRes.status === 'fulfilled') {
        renkoSessionIds = new Set(renkoRes.value);
      }
      if (parityRes.status === 'fulfilled') {
        parityStatuses = new Map(parityRes.value.map((p) => [p.sessionId, p.status]));
      }
    } finally {
      refreshing = false;
      loading = false;
    }
  }

  onMount(async () => {
    await fetchData();
    // livePriceStore lifecycle vive en AppShell (start global + refresh
    // periodico de simbolos de TODAS las sessions live). Aca el setSymbols
    // dentro de fetchData no es necesario para el ticker pero lo mantenemos:
    // en /live especificamente, si el fetch detecta nuevos simbolos antes
    // que el shell, los suscribimos inmediatamente (mejor UX).
    scheduled = scheduleAlignedRefresh(async () => {
      await fetchData(true);
    }, 5);
  });

  onDestroy(() => {
    scheduled?.cancel();
    // NO limpiamos symbols: AppShell mantiene la suscripcion global
    // (todas las sessions live) en su propio refresh aligned :05.
  });

  // ----- opciones de los dropdowns (cascade) -----
  let availableAccounts = $derived.by((): string[] => {
    const set = new Set<string>();
    for (const s of allSessions) {
      if (s.accountName) set.add(s.accountName);
    }
    return [...set].sort();
  });

  let availableSymbols = $derived.by((): string[] => {
    const set = new Set<string>();
    for (const s of allSessions) {
      const accountOk =
        selectedAccounts.size === 0 ||
        (s.accountName != null && selectedAccounts.has(s.accountName));
      if (accountOk) set.add(s.symbol);
    }
    return [...set].sort();
  });

  let availableTitles = $derived.by((): string[] => {
    const set = new Set<string>();
    for (const s of allSessions) {
      const accountOk =
        selectedAccounts.size === 0 ||
        (s.accountName != null && selectedAccounts.has(s.accountName));
      const symbolOk = selectedSymbols.size === 0 || selectedSymbols.has(s.symbol);
      const title = s.customTitle ?? '';
      if (accountOk && symbolOk && title) set.add(title);
    }
    return [...set].sort();
  });

  // Purga selecciones obsoletas cuando cambia el universo upstream (cascade).
  $effect(() => {
    const avail = new Set(availableSymbols);
    let changed = false;
    const next = new Set<string>();
    for (const s of selectedSymbols) {
      if (avail.has(s)) next.add(s);
      else changed = true;
    }
    if (changed) selectedSymbols = next;
  });

  $effect(() => {
    const avail = new Set(availableTitles);
    let changed = false;
    const next = new Set<string>();
    for (const t of selectedTitles) {
      if (avail.has(t)) next.add(t);
      else changed = true;
    }
    if (changed) selectedTitles = next;
  });

  // ----- filtered + sorted -----
  function matchesFilters(s: LiveSessionCard): boolean {
    if (
      selectedAccounts.size > 0 &&
      !(s.accountName != null && selectedAccounts.has(s.accountName))
    ) {
      return false;
    }
    if (selectedSymbols.size > 0 && !selectedSymbols.has(s.symbol)) return false;
    if (selectedTitles.size > 0 && !(s.customTitle && selectedTitles.has(s.customTitle))) {
      return false;
    }
    if (openOnly && s.openPositions === 0) return false;
    // Live Monitor solo trae sessions con isLive=true (filtro server-side),
    // por eso lo pasamos como true al helper sin checkear el campo (que
    // LiveSessionCard no expone).
    if (activeOnly && !isSessionActiveNow({ isLive: true, ...s })) return false;
    return true;
  }

  // Paridad con v1 (parseTimeToMinutes): convierte 'HH:mm' a minutos del dia.
  // Sentinela 9999 manda al final las sessions sin dailyStartTime (igual que v1).
  function parseTimeToMinutes(timeStr: string | null): number {
    if (!timeStr) return 9999;
    const parts = timeStr.split(':');
    const h = Number(parts[0]);
    const m = Number(parts[1]);
    if (!Number.isFinite(h) || !Number.isFinite(m)) return 9999;
    return h * 60 + m;
  }

  let filtered = $derived.by((): LiveSessionCard[] => {
    const arr = allSessions.filter(matchesFilters);
    if (sortByTime) {
      // Sort numerico por minutos del dia de dailyStartTime. Sin dato -> al final.
      arr.sort(
        (a, b) => parseTimeToMinutes(a.dailyStartTime) - parseTimeToMinutes(b.dailyStartTime),
      );
    } else {
      // Default: startTime desc (mas reciente arriba).
      arr.sort((a, b) => (b.startTime ?? '').localeCompare(a.startTime ?? ''));
    }
    return arr;
  });

  // Aggregate "Open Only"-aware (audit fix #2): si openOnly esta activo,
  // recalculamos el aggregate sobre el set filtrado en cliente.
  // Si no, usamos el aggregate del backend (todas las sessions del modo).
  let displayedAggregate = $derived.by((): LiveMonitorAggregate => {
    if (!openOnly && !activeOnly && selectedAccounts.size === 0 && selectedSymbols.size === 0 && selectedTitles.size === 0) {
      // Sin filtros: usar el aggregate del backend tal cual.
      return aggregate ?? emptyAggregate();
    }
    return recomputeAggregate(filtered);
  });

  function emptyAggregate(): LiveMonitorAggregate {
    return {
      activeSessionsCount: 0,
      totalPnL: 0,
      totalBtPnL: 0,
      totalTrades: 0,
      totalWins: 0,
      totalLosses: 0,
      totalOpenPositions: 0,
      totalWinAmount: 0,
      totalLossAmount: 0,
      winRate: 0,
      profitFactor: null,
    };
  }

  function recomputeAggregate(arr: LiveSessionCard[]): LiveMonitorAggregate {
    let totalPnL = 0;
    let totalBtPnL = 0;
    let totalTrades = 0;
    let totalWins = 0;
    let totalOpenPositions = 0;
    let totalWinAmount = 0;
    let totalLossAmount = 0;
    for (const s of arr) {
      totalPnL += s.totalPnL;
      totalBtPnL += s.totalBtPnL;
      totalTrades += s.tradeCount;
      totalWins += s.wins;
      totalOpenPositions += s.openPositions;
      totalWinAmount += s.winAmount;
      totalLossAmount += s.lossAmount;
    }
    const totalLosses = Math.max(0, totalTrades - totalWins - totalOpenPositions);
    return {
      activeSessionsCount: arr.length,
      totalPnL,
      totalBtPnL,
      totalTrades,
      totalWins,
      totalLosses,
      totalOpenPositions,
      totalWinAmount,
      totalLossAmount,
      winRate: totalTrades > 0 ? (totalWins / totalTrades) * 100 : 0,
      profitFactor: totalLossAmount > 0 ? totalWinAmount / totalLossAmount : null,
    };
  }

  // ----- live unrealized total (suma sobre filtered) -----
  // Devuelve { value, reason }. Si al menos un trade computa OK, value es
  // la suma parcial (mejor mostrar un numero parcial que un N/A total).
  // reason solo se setea cuando NO se pudo computar NADA; la UI lo usa
  // para mostrar "Connecting…" / "No vpp" / "Waiting tick" en vez de "N/A".
  let liveUnrealizedStatus = $derived.by((): {
    value: number | null;
    reason: UnrealizedPnLReason;
  } => {
    let sum = 0;
    let anyValid = false;
    let firstReason: UnrealizedPnLReason = null;
    let anyOpenTrade = false;
    for (const s of filtered) {
      const px = livePriceStore.prices.get(s.symbol);
      const livePrice = px?.price ?? null;
      for (const t of s.openTrades) {
        anyOpenTrade = true;
        const input = {
          side: t.side,
          entryPrice: t.entryPrice,
          quantity: t.quantity,
          valuePerPoint: t.valuePerPoint,
          livePrice,
        };
        const pnl = unrealizedPnL(input);
        if (pnl == null) {
          const r = unrealizedPnLReason(input);
          if (firstReason == null || (firstReason === 'waiting-price' && r === 'no-vpp')) {
            firstReason = r;
          }
          continue;
        }
        sum += pnl;
        anyValid = true;
      }
    }
    if (anyValid) return { value: sum, reason: null };
    if (!anyOpenTrade) return { value: null, reason: null };
    return { value: null, reason: firstReason };
  });
  let liveUnrealizedTotal = $derived(liveUnrealizedStatus.value);
  let liveUnrealizedReason = $derived(liveUnrealizedStatus.reason);

  // ----- account breakdown (chips en aggregate bar) -----
  let accountBreakdown = $derived.by(() => {
    const byAccount = new Map<string, { count: number; symbols: Set<string> }>();
    for (const s of filtered) {
      const acc = s.accountName ?? '—';
      let entry = byAccount.get(acc);
      if (!entry) {
        entry = { count: 0, symbols: new Set() };
        byAccount.set(acc, entry);
      }
      entry.count++;
      entry.symbols.add(s.symbol);
    }
    return [...byAccount.entries()].map(([account, v]) => ({
      account,
      count: v.count,
      symbols: [...v.symbols].sort(),
    }));
  });

  // ----- handlers -----
  function toggleSet(set: Set<string>, value: string): Set<string> {
    const next = new Set(set);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    return next;
  }

  function toggleAccount(a: string): void {
    selectedAccounts = toggleSet(selectedAccounts, a);
  }
  function toggleSymbol(s: string): void {
    selectedSymbols = toggleSet(selectedSymbols, s);
  }
  function toggleTitle(t: string): void {
    selectedTitles = toggleSet(selectedTitles, t);
  }

  function clearFilters(): void {
    selectedAccounts = new Set();
    selectedSymbols = new Set();
    selectedTitles = new Set();
  }

  function toggleCard(sessionId: string): void {
    expandedIds = toggleSet(expandedIds, sessionId);
  }

  function collapseAll(): void {
    expandedIds = new Set();
  }

  function expandAll(): void {
    expandedIds = new Set(filtered.map((s) => s.sessionId));
  }

  function fmtLastFetch(d: Date | null): string {
    if (!d) return '—';
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  }

  let isDev = $derived(mode === 'developer');
  // useDevLook: apariencia cyan. SIEMPRE true para developer mode; en live
  // depende del toggle "Live looks like Dev" (preferencia de usuario).
  let useDevLook = $derived(isDev || liveLookStore.liveLooksLikeDev);
  let pageTitle = $derived(isDev ? 'Developer Monitor' : 'Live Monitor');
  let pageSubtitle = $derived(
    isDev
      ? 'Dev sessions, real-time. Auto-refresh every minute.'
      : 'Live sessions, real-time. Live price polled every 5s.',
  );
</script>

<AppShell>
  <section class="mx-auto w-full max-w-[1400px] px-4 py-8 md:px-8 md:py-10">
    <header class="mb-8 flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
      <div class="flex flex-col gap-1">
        <span class={['text-eyebrow', useDevLook && 'text-cyan-400/90'].filter(Boolean).join(' ')}>
          {isDev ? 'Developer Stream' : 'Real-Time'}
        </span>
        <h1
          class={[
            'text-display text-3xl md:text-4xl',
            useDevLook && 'text-cyan-300',
          ]
            .filter(Boolean)
            .join(' ')}
        >
          {pageTitle}
        </h1>
        <p class="text-muted-foreground mt-1 text-[13px]">{pageSubtitle}</p>
      </div>
      <div class="flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          disabled={refreshing}
          onclick={() => fetchData()}
          title={lastFetch ? `Last refresh: ${fmtLastFetch(lastFetch)}` : undefined}
        >
          <RefreshCw class={['size-3.5', refreshing && 'animate-spin'].filter(Boolean).join(' ')} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </Button>
      </div>
    </header>

    {#if loading}
      <div class="space-y-4">
        <Skeleton class="h-24 w-full" />
        <Skeleton class="h-40 w-full" />
        <Skeleton class="h-40 w-full" />
      </div>
    {:else if error}
      <div
        role="alert"
        class="border-destructive/40 bg-destructive/10 text-destructive rounded-md border px-4 py-3 text-sm"
      >
        Error loading live monitor: {error}
      </div>
    {:else}
      <div class="space-y-4">
        <LiveMonitorAggregateBar
          aggregate={displayedAggregate}
          {liveUnrealizedTotal}
          {liveUnrealizedReason}
          {accountBreakdown}
          {mode}
        />

        <!-- Filtros -->
        <div class="flex flex-col gap-2">
          <!-- Account multi -->
          {#if availableAccounts.length > 1}
            <div class="flex flex-wrap items-center gap-1.5">
              <span class="text-muted-foreground mr-1 text-[10px] uppercase tracking-wider">
                Account
              </span>
              {#each availableAccounts as a (a)}
                {@const active = selectedAccounts.has(a)}
                <button
                  type="button"
                  class={[
                    'rounded-md border px-2 py-1 text-xs transition-colors',
                    active
                      ? 'bg-primary/15 text-primary border-primary/40'
                      : 'text-muted-foreground hover:text-foreground hover:border-foreground/30',
                  ].join(' ')}
                  onclick={() => toggleAccount(a)}
                >
                  {a}
                </button>
              {/each}
            </div>
          {/if}

          <!-- Symbol multi (filtrado por account) -->
          {#if availableSymbols.length > 1}
            <div class="flex flex-wrap items-center gap-1.5">
              <span class="text-muted-foreground mr-1 text-[10px] uppercase tracking-wider">
                Symbol
              </span>
              {#each availableSymbols as s (s)}
                {@const active = selectedSymbols.has(s)}
                <button
                  type="button"
                  class={[
                    'rounded-md border px-2 py-1 font-mono text-xs transition-colors',
                    active
                      ? 'bg-primary/15 text-primary border-primary/40'
                      : 'text-muted-foreground hover:text-foreground hover:border-foreground/30',
                  ].join(' ')}
                  onclick={() => toggleSymbol(s)}
                >
                  {s}
                </button>
              {/each}
            </div>
          {/if}

          <!-- Title multi (filtrado por account+symbol) -->
          {#if availableTitles.length > 0}
            <div class="flex flex-wrap items-center gap-1.5">
              <span class="text-muted-foreground mr-1 text-[10px] uppercase tracking-wider">
                Title
              </span>
              {#each availableTitles as t (t)}
                {@const active = selectedTitles.has(t)}
                <button
                  type="button"
                  class={[
                    'rounded-md border px-2 py-1 text-xs italic transition-colors',
                    active
                      ? 'bg-primary/15 text-primary border-primary/40'
                      : 'text-muted-foreground hover:text-foreground hover:border-foreground/30',
                  ].join(' ')}
                  onclick={() => toggleTitle(t)}
                >
                  {t}
                </button>
              {/each}
            </div>
          {/if}

          <!-- Toggles -->
          <div class="flex flex-wrap items-center gap-2 pt-1">
            <Button
              variant={sortByTime ? 'secondary' : 'ghost'}
              size="sm"
              onclick={() => {
                sortByTime = !sortByTime;
              }}
            >
              <ArrowDownUp class="size-3.5" />
              Sort by time
            </Button>
            <Button
              variant={openOnly ? 'secondary' : 'ghost'}
              size="sm"
              onclick={() => {
                openOnly = !openOnly;
              }}
            >
              Open only
              {#if openOnly}
                <Badge variant="outline" class="ml-1">{filtered.length}</Badge>
              {/if}
            </Button>
            <Button
              variant={activeOnly ? 'secondary' : 'ghost'}
              size="sm"
              onclick={() => {
                activeOnly = !activeOnly;
              }}
              title="Sessions whose trading window includes now (ET)"
            >
              Active only
              {#if activeOnly}
                <Badge variant="outline" class="ml-1">{filtered.length}</Badge>
              {/if}
            </Button>

            <span class="text-muted-foreground/40">·</span>

            <Button variant="ghost" size="sm" onclick={collapseAll}>
              <ChevronsUp class="size-3.5" />
              Collapse all
            </Button>
            <Button variant="ghost" size="sm" onclick={expandAll}>
              <ChevronsDown class="size-3.5" />
              Expand all
            </Button>

            {#if selectedAccounts.size + selectedSymbols.size + selectedTitles.size > 0}
              <span class="text-muted-foreground/40">·</span>
              <Button variant="ghost" size="sm" onclick={clearFilters}>Clear filters</Button>
            {/if}
          </div>
        </div>

        <!-- Cards -->
        <div class="relative space-y-3">
          {#if refreshing}
            <div
              class="bg-background/40 pointer-events-none absolute inset-0 z-10 backdrop-blur-[1px]"
              aria-hidden="true"
            ></div>
          {/if}

          {#if filtered.length === 0}
            <p class="text-muted-foreground py-12 text-center text-sm">
              No {mode === 'developer' ? 'dev' : 'live'} sessions match the filters.
            </p>
          {:else}
            {#each filtered as s (s.sessionId)}
              {@const px = livePriceStore.prices.get(s.symbol) ?? null}
              <SessionCardCmp
                session={s}
                {mode}
                livePrice={px?.price ?? null}
                expanded={expandedIds.has(s.sessionId)}
                onToggle={toggleCard}
                parityStatus={parityStatuses.get(s.sessionId) ?? null}
                renkoExists={renkoSessionIds.has(s.sessionId)}
                compareCandidates={allSessions}
              />
            {/each}
          {/if}
        </div>
      </div>
    {/if}
  </section>
</AppShell>
